import React from 'react';
import isEmpty from 'lodash/isEmpty';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { ThunkDispatch } from 'redux-thunk';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import isNull from 'lodash/isNull';
import differenceInDays from 'date-fns/differenceInDays';
import { Action } from 'redux';
import { LabeledValue, SelectValue } from 'antd/lib/select';
import {
  condoFiltersActions,
  condosActions,
  getCondos,
  ICondoFiltersState,
  ICondosLocationPickerState,
  condosLocationPickerActions,
  ICondoGuestsState,
  ICondosState,
  IMenuState,
} from 'src/store/slices/index';
import { RootState } from 'src/store';
import {
  CondoLocationsEnum,
  CondoMatchTypeEnum,
  CondoRequestTypeEnum,
  IBounds,
  ICondo,
  SearchTypeEnum,
} from 'src/common-types/index';
import {
  CondoExpiredSessionMessage,
  CustomSelect,
  WhiteButton,
  BlueButton,
  FilterCheckbox,
} from 'src/components/components/index';
import { CloseSvg, FilterSvg, MapSvg, ArrowTopSvg, ArrowBottomSvg } from 'src/assets/index';
import {
  getCondoSortOptions,
  UrlUtils,
  isCondoFiltersEmpty,
  getCondoFiltersCount,
  getBudgetRangeLabel,
} from 'src/utils/index';
import CondoMapPlaceholder from 'src/assets/images/condo-map-placeholder.jpg';
import MapMarker from 'src/assets/images/map-price-icon.svg';
import MapMarkerSelected from 'src/assets/images/map-price-selected-icon.svg';
import MapHomeMarker from 'src/assets/images/map-home-icon.svg';
import MapHomeMarkerSelected from 'src/assets/images/max-home-selected-icon.svg';
import {
  APP_SELECTOR,
  CONDO_FILTERS_LABEL,
  DEFAULT_PAGE_SIZE,
  SortTypes,
} from 'src/constants/index';
import { CondoSkeletonResult } from '../condo-skeleton-result';
import {
  NoAvailableCondosNotification,
  TravelDatesNotification,
  NoAvailableCondosFilterNotification,
} from '../notification';
import { CondoHotelCard } from '../condo-hotel-card';
import { CondoCardsSkeleton } from '../condo-card-skeleton';
import { CondoMapWrapper } from '../condo-map';
import { CondoFiltersWrapper } from '../condo-filters-wrapper';
import { CondoSkeletonFilters } from '../condo-skeleton-filters';
import './style.scss';

interface IMapStateToProps {
  condosStore: ICondosState;
  condosLocationPickerStore: ICondosLocationPickerState;
  condoFiltersStore: ICondoFiltersState;
  condoGuestsStore: ICondoGuestsState;
  menuStore: IMenuState;
}

interface IMapDispatchToProps {
  setSortType: (sortType: SortTypes) => void;
  setPageNumber: (num: number) => void;
  getCondos: (searchType?: SearchTypeEnum) => void;
  setBounds: (bounds: IBounds) => void;
  setSelectedCondo: (condo: ICondo) => void;
  addMapCondo: (condo: ICondo) => void;
  resetFilters: () => void;
  setBudgetRanges: (value: string[]) => void;
  setDefaultValues: () => void;
  resetLocations: () => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps {
  matches?: boolean;
}

interface IState {
  visibleFilters: boolean;
  visibleMap: boolean;
  isPriceFilterOpen: boolean;
  isNoAvailableCondosNotification: boolean;
}

const debounceTime = 300;
const week = 7;
const scrollPixels = 150;
const scrollWindowPixels = 350;
const zero = 0;
const one = 1;
const topZIndex = 5;
const iconWidth = 33;
const CHECK_WIDTH = 1020;
const minusOne = -1;

class CondoResultWrapperComponent extends React.Component<IProps, IState> {
  state: IState = {
    visibleFilters: false,
    visibleMap: false,
    isPriceFilterOpen: false,
    isNoAvailableCondosNotification: false,
  };
  priceFilterWrapperRef: React.RefObject<HTMLDivElement> = React.createRef();
  contentWrapperRef: React.RefObject<HTMLDivElement> = React.createRef();
  root: HTMLDivElement = document.querySelector(APP_SELECTOR);
  prevFilters: ICondoFiltersState = null;

  handleResize = debounce(() => {
    const footer: HTMLDivElement = document.querySelector('.footer');
    this.showBodyScroll(!!(window.innerWidth <= CHECK_WIDTH && footer));
  }, debounceTime);

  togglePriceFilter = () => {
    this.setState((prevState: IState) => ({ isPriceFilterOpen: !prevState.isPriceFilterOpen }));
  };

  showBodyScroll = (show: boolean, withScroll = true) => {
    const footer: HTMLDivElement = document.querySelector('.footer');

    if (show) {
      document.body.style.overflow = 'auto';
      if (!isNull) {
        footer.style.display = 'block';
      }
    } else {
      if (withScroll) {
        window.scrollTo(zero, zero);
      }
      const values = UrlUtils.getValues();

      if (!isEmpty(values) && this.props.matches) {
        document.body.style.overflow = 'hidden';
      }

      if (!isNull) {
        footer.style.display = 'none';
      }
    }
  };

  toggleFilters = () => {
    this.setState({ visibleFilters: !this.state.visibleFilters }, () => {
      this.showBodyScroll(!this.state.visibleFilters && !this.state.visibleMap, false);
    });
  };

  showMap = () => {
    this.setState({ visibleMap: !this.state.visibleMap }, () => {
      this.showBodyScroll(!this.state.visibleMap, false);
    });
  };

  onWindowScroll = debounce(() => {
    const { condosLocationPickerStore, condosStore, setPageNumber, getCondos } = this.props;
    const { selectedLocation } = condosLocationPickerStore;
    const {
      isSessionExpired,
      condos,
      loadingMore,
      loading,
      pageNumber,
      isFinishedWithError,
      prevCondos,
    } = condosStore;
    const { current } = this.contentWrapperRef;

    if (!selectedLocation) {
      return;
    }

    if (
      (!prevCondos || prevCondos?.length > zero) &&
      !isFinishedWithError &&
      current &&
      !isSessionExpired &&
      condos &&
      condos.length &&
      condos.length % DEFAULT_PAGE_SIZE === zero &&
      window.innerWidth <= CHECK_WIDTH &&
      !loadingMore &&
      !loading &&
      window.innerHeight + window.scrollY + scrollWindowPixels >= this.root.offsetHeight
    ) {
      setPageNumber(pageNumber + one);
      getCondos();
    }
  }, debounceTime);

  onScroll = debounce(() => {
    const { condosLocationPickerStore, condosStore, setPageNumber, getCondos } = this.props;
    const { selectedLocation } = condosLocationPickerStore;
    const {
      isSessionExpired,
      condos,
      loadingMore,
      loading,
      pageNumber,
      isFinishedWithError,
      prevCondos,
      cachedSelectedLocation,
    } = condosStore;
    const { current } = this.contentWrapperRef;

    if (!selectedLocation && !cachedSelectedLocation) {
      return;
    }

    if (
      (!prevCondos || prevCondos?.length > zero) &&
      !isFinishedWithError &&
      current &&
      !isSessionExpired &&
      condos &&
      condos.length &&
      condos.length % DEFAULT_PAGE_SIZE === zero &&
      window.innerWidth > CHECK_WIDTH &&
      !loadingMore &&
      !loading &&
      current.scrollTop + current.clientHeight > current.scrollHeight - scrollPixels
    ) {
      setPageNumber(pageNumber + one);
      getCondos();
    }
  }, debounceTime);

  handleClickOutside = (event: MouseEvent): void => {
    if (
      this.state.isPriceFilterOpen &&
      this.priceFilterWrapperRef &&
      !this.priceFilterWrapperRef.current.contains(event.target as Node)
    ) {
      this.togglePriceFilter();
    }
  };

  componentDidMount() {
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
    this.contentWrapperRef?.current?.addEventListener('scroll', this.onScroll);
    window.addEventListener('scroll', this.onWindowScroll);
    document.addEventListener('mousedown', this.handleClickOutside);
    this.setState({
      isNoAvailableCondosNotification: isCondoFiltersEmpty(this.props.condoFiltersStore),
    });
  }

  componentWillUnmount() {
    const { setSelectedCondo, setDefaultValues, resetLocations } = this.props;
    window.removeEventListener('resize', this.handleResize);
    this.contentWrapperRef?.current?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('scroll', this.onWindowScroll);
    setSelectedCondo(null);
    document.removeEventListener('mousedown', this.handleClickOutside);
    setTimeout(() => {
      document.body.style.overflow = 'auto';
    }, 10);
    // setDefaultValues();
    // resetLocations();
  }

  componentDidUpdate(prevProps: Readonly<IProps>) {
    const { loading } = this.props.condosStore;

    if (prevProps.condosStore.loading !== loading && !loading) {
      this.setState({
        isNoAvailableCondosNotification: isCondoFiltersEmpty(this.props.condoFiltersStore),
      });
    }
  }

  getSortOptions = (): LabeledValue[] => {
    return getCondoSortOptions(this.props.condosStore);
  };

  onSortChange = (value: SelectValue): void => {
    this.props.setSortType(value as SortTypes);
    this.props.setPageNumber(one);

    if (this.props.matches) {
      this.onFiltersOrSortChange();
    }
  };

  updateBounds = (bounds: IBounds): void => {
    const { setBounds, getCondos } = this.props;

    setBounds(bounds);
    getCondos(SearchTypeEnum.MapSearch);
  };

  onMouseEnterCondoCard = (id: number): void => {
    if (this.props.condosStore.selectedCondo?.condoId !== id) {
      const marker: HTMLElement = document.querySelector(`[data-item-id="${id}"]`);

      if (marker && !marker.dataset.itemNotChange) {
        marker.style.zIndex = `${topZIndex}`;

        if (this.props.condosStore.lastRequestType === CondoRequestTypeEnum.Anytime) {
          marker.style.background = `url(${MapHomeMarkerSelected})`;
          marker.style.width = `${iconWidth}px`;
        } else {
          marker.style.background = `url(${MapMarkerSelected})`;
        }
      }
    }
  };

  onMouseLeaveCondoCard = (id: number): void => {
    if (this.props.condosStore.selectedCondo?.condoId !== id) {
      const marker: HTMLElement = document.querySelector(`[data-item-id="${id}"]`);

      if (marker && !marker.dataset.itemNotChange) {
        marker.style.zIndex = `${one}`;

        if (this.props.condosStore.lastRequestType === CondoRequestTypeEnum.Anytime) {
          marker.style.background = `url(${MapHomeMarker})`;
          marker.style.width = `${iconWidth}px`;
        } else {
          marker.style.background = `url(${MapMarker})`;
        }
      }
    }
  };

  selectCondo = (condo: ICondo) => {
    const { setSelectedCondo, condosStore, addMapCondo, matches } = this.props;
    const { lastRequestType, mapCondos } = condosStore;
    const markers = document.querySelectorAll('.price-marker');

    if (markers && markers.length) {
      markers.forEach((marker: HTMLDivElement) => {
        if (marker.dataset.itemNotChange) {
          return;
        }

        marker.style.zIndex = `${one}`;

        if (lastRequestType === CondoRequestTypeEnum.Anytime) {
          marker.style.background = `url(${MapHomeMarker})`;
          marker.style.width = `${iconWidth}px`;
        } else {
          marker.style.background = `url(${MapMarker})`;
        }
      });
    }

    setSelectedCondo(condo);

    if (condo) {
      if (mapCondos.every(({ condoId }) => condoId !== condo.condoId)) {
        addMapCondo(condo);
      }

      const selectedMarker: HTMLDivElement = document.querySelector(
        `.price-marker[data-item-id="${condo.condoId}"]`,
      );

      if (selectedMarker && !selectedMarker.dataset.itemNotChange) {
        selectedMarker.style.zIndex = `${topZIndex}`;

        if (lastRequestType === CondoRequestTypeEnum.Anytime) {
          selectedMarker.style.background = `url(${MapHomeMarkerSelected})`;
          selectedMarker.style.width = `${iconWidth}px`;
        } else {
          selectedMarker.style.background = `url(${MapMarkerSelected})`;
        }
      }

      if (!matches && !this.state.visibleMap) {
        setTimeout(() => {
          this.showMap();
        });
      }
    }
  };

  onFiltersOrSortChange = debounce(() => {
    const { condoFiltersStore, getCondos } = this.props;

    if (!isEqual(this.prevFilters, condoFiltersStore)) {
      UrlUtils.setUrl(CONDO_FILTERS_LABEL, condoFiltersStore);
      this.prevFilters = condoFiltersStore;
      this.props.setPageNumber(one);
      getCondos(SearchTypeEnum.SortsFilters);
      this.setState({ isNoAvailableCondosNotification: false });
    }
  }, debounceTime);

  resetFilters = () => {
    this.props.resetFilters();
    this.onFiltersOrSortChange();
  };

  onBudgetChange = (value: string[]) => {
    this.props.setBudgetRanges(value);
  };

  render(): React.ReactNode {
    const { condosStore, matches, condoFiltersStore, condoGuestsStore, menuStore } = this.props;
    const { petsAllowed } = condoGuestsStore;
    const {
      condos,
      loading,
      bounds,
      cachedSelectedLocation,
      isSessionExpired,
      loadingMore,
      counters,
      lastRequestType,
      cachedEndDate,
      cachedStartDate,
      selectedCondo,
      mapCondos,
    } = condosStore;
    const { sortType, budgetRanges } = condoFiltersStore;
    const { visibleFilters, visibleMap, isPriceFilterOpen, isNoAvailableCondosNotification } =
      this.state;
    const isAnytime = lastRequestType === CondoRequestTypeEnum.Anytime;
    const isStrict = lastRequestType === CondoRequestTypeEnum.Strict;
    const nightsCount =
      isStrict && cachedStartDate && cachedEndDate
        ? differenceInDays(new Date(cachedEndDate), new Date(cachedStartDate))
        : zero;
    const isSpecificCondo =
      cachedSelectedLocation?.locationType === CondoLocationsEnum.GenericCondo;
    const exactMatches: ICondo[] = condos.filter(
      (condo: ICondo) => condo.matchType === CondoMatchTypeEnum.Exact,
    );
    const alternativesMatches: ICondo[] = condos.filter(
      (condo: ICondo) => condo.matchType === CondoMatchTypeEnum.Alternative,
    );
    const haveExacts = exactMatches.length > zero;
    const totalCondos = haveExacts
      ? counters?.totalFilteredExactCondos
      : counters?.totalFilteredCondos;

    const totalOptions = haveExacts
      ? counters?.totalFilteredExactOptions
      : counters?.totalFilteredOptions;

    return (
      <div className="condo-result-wrapper">
        <div className="condo-result-wrapper__wrapper">
          <div
            ref={this.contentWrapperRef}
            className={`condo-result-wrapper__content ${visibleFilters ? 'filters-content' : ''}`}
          >
            <div
              className={`condo-result-wrapper__filters-wrapper ${visibleFilters ? 'show' : ''}`}
            >
              {visibleFilters ? (
                loading ? (
                  <CondoSkeletonFilters />
                ) : (
                  <CondoFiltersWrapper
                    onClose={this.toggleFilters}
                    onFiltersOrSortChange={this.onFiltersOrSortChange}
                    isDisabled={isSessionExpired}
                    matches={matches}
                  />
                )
              ) : null}
            </div>
            <div
              className={`condo-result-wrapper__hotels-wrapper ${!visibleFilters ? 'show' : ''}`}
            >
              {!matches && this.state.visibleMap && (
                <div className="condo-result-wrapper__map-top">
                  <div className="condo-result-wrapper__map-close" onClick={this.showMap}>
                    <CloseSvg />
                  </div>
                  <div className="condo-result-wrapper__map-location">
                    {cachedSelectedLocation?.name}
                  </div>
                  <div
                    className={`condo-result-wrapper__map-filters ${
                      isSessionExpired ? 'disabled' : ''
                    }`}
                    onClick={this.toggleFilters}
                  >
                    <FilterSvg />
                  </div>
                </div>
              )}
              {loading ? (
                <CondoSkeletonResult />
              ) : (
                <>
                  {isSessionExpired && (
                    <div className="condo-result-wrapper__session-expired">
                      <CondoExpiredSessionMessage onRefresh={() => window.location.reload()} />
                    </div>
                  )}
                  {matches && isAnytime ? <TravelDatesNotification /> : null}
                  <div
                    className={`condo-result-wrapper__btn-view-wrapper ${
                      isSessionExpired ? 'disabled' : ''
                    }`}
                  >
                    {!isAnytime && !isSessionExpired && (
                      <div className="condo-result-wrapper__fast-filter-wrapper">
                        <div
                          className={`condo-result-wrapper__fast-filter-top ${
                            isPriceFilterOpen ? 'open' : ''
                          }`}
                          onClick={this.togglePriceFilter}
                        >
                          <div className="condo-result-wrapper__fast-filter-top-tittle">
                            <FormattedMessage id="price" />
                          </div>
                          {isPriceFilterOpen ? <ArrowTopSvg /> : <ArrowBottomSvg />}
                        </div>
                        <div
                          ref={this.priceFilterWrapperRef}
                          className={`condo-result-wrapper__fast-filter-dropdown ${
                            isPriceFilterOpen ? 'open' : ''
                          }`}
                        >
                          <div className="condo-result-wrapper__fast-filter-dropdown-top">
                            <FilterCheckbox
                              disabled={isSessionExpired || isAnytime}
                              onChange={this.onBudgetChange}
                              values={budgetRanges}
                              options={counters.budgetRanges.map(({ key }) => {
                                return {
                                  count: minusOne,
                                  value: `${key.from}-${key.to}`,
                                  label: getBudgetRangeLabel(key),
                                };
                              })}
                              title="any.price"
                            />
                          </div>
                          <div className="condo-result-wrapper__fast-filter-dropdown-bottom">
                            <BlueButton
                              onClick={() => {
                                this.togglePriceFilter();
                                this.onFiltersOrSortChange();
                              }}
                            >
                              <FormattedMessage id="done" />
                            </BlueButton>
                          </div>
                        </div>
                      </div>
                    )}
                    <WhiteButton onClick={this.toggleFilters} disabled={isSessionExpired}>
                      {isCondoFiltersEmpty(condoFiltersStore) ? (
                        <FilterSvg />
                      ) : (
                        <span className="condo-result-wrapper__filters-count">
                          {getCondoFiltersCount(condoFiltersStore)}
                        </span>
                      )}
                      <FormattedMessage id="all.filters" />
                    </WhiteButton>
                    {!isCondoFiltersEmpty(condoFiltersStore) && (
                      <div className="condo-result-wrapper__reset-all" onClick={this.resetFilters}>
                        <FormattedMessage id="reset.all.filters" />
                      </div>
                    )}
                  </div>
                  <div
                    className={`condo-result-wrapper__btn-view-wrapper mobile ${
                      isSessionExpired ? 'disabled' : ''
                    }`}
                  >
                    <WhiteButton onClick={this.toggleFilters} disabled={isSessionExpired}>
                      <FilterSvg />
                      <FormattedMessage id="sort.filter" />
                    </WhiteButton>
                    <WhiteButton onClick={this.showMap} disabled={isSessionExpired}>
                      <MapSvg />
                      <FormattedMessage id="view.map" />
                    </WhiteButton>
                  </div>
                  {!matches && isAnytime && <TravelDatesNotification />}
                  {condos && !loading ? (
                    <>
                      {haveExacts && (
                        <div className="condo-result-wrapper__condos-top-section">
                          <div
                            className={`condo-result-wrapper__condos-count ${
                              isSessionExpired ? 'disabled' : ''
                            }`}
                          >
                            {cachedSelectedLocation?.name}
                            {': '}
                            {isSpecificCondo ? (
                              <FormattedMessage
                                values={{ resortsCount: totalCondos, optionsCount: totalOptions }}
                                id="options.found.in"
                              />
                            ) : (
                              <FormattedMessage
                                values={{ resortsCount: totalCondos, optionsCount: totalOptions }}
                                id="options.available.in"
                              />
                            )}
                          </div>
                          {matches && (
                            <div className="condo-result-wrapper__condos-sort">
                              <div className="condo-result-wrapper__sort-select">
                                <span className="condo-result-wrapper__sort-label">
                                  <FormattedMessage id="sort.by" />:{' '}
                                </span>
                                <CustomSelect
                                  disabled={isSessionExpired || (isAnytime && !isSpecificCondo)}
                                  value={sortType}
                                  onChange={this.onSortChange}
                                  options={this.getSortOptions()}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {haveExacts ? (
                        exactMatches.map((condo: ICondo, index) => {
                          return (
                            <CondoHotelCard
                              onSelect={() => this.selectCondo(condo)}
                              disabled={isSessionExpired}
                              key={`${condo.condoId}-${index}`}
                              condo={condo}
                              lastRequestType={lastRequestType}
                              onMouseLeave={() => this.onMouseLeaveCondoCard(condo.condoId)}
                              onMouseEnter={() => this.onMouseEnterCondoCard(condo.condoId)}
                              matches={matches}
                            />
                          );
                        })
                      ) : isCondoFiltersEmpty(condoFiltersStore) &&
                        isNoAvailableCondosNotification ? (
                        <NoAvailableCondosNotification
                          baseUrl={menuStore.items?.baseUrl}
                          isPetsAllowedError={isSpecificCondo && petsAllowed}
                          resortsCount={counters?.totalAlternativeCondos}
                          optionsCount={counters?.totalAlternativeOptions}
                          isSpecificCondo={isSpecificCondo}
                        />
                      ) : (
                        <NoAvailableCondosFilterNotification
                          resetFilters={this.resetFilters}
                          altOptionsCount={alternativesMatches.length}
                        />
                      )}
                      {alternativesMatches && alternativesMatches.length ? (
                        <>
                          <div className="condo-result-wrapper__condos-top-section">
                            <div
                              className={`condo-result-wrapper__condos-count alternative ${
                                isSessionExpired ? 'disabled' : ''
                              }`}
                            >
                              {isSpecificCondo ? (
                                <FormattedMessage
                                  values={{
                                    resortsCount: counters?.totalFilteredAlternativeCondos,
                                    optionsCount: counters?.totalFilteredAlternativeOptions
                                  }}
                                  id="and.options.available.in"
                                />
                              ) : haveExacts ? (
                                <FormattedMessage
                                  values={{
                                    resortsCount: counters?.totalFilteredAlternativeCondos,
                                    optionsCount: counters?.totalFilteredAlternativeOptions,
                                    location: cachedSelectedLocation?.name,
                                  }}
                                  id="alternative.condo.title"
                                />
                              ) : null}{' '}
                              {isStrict && nightsCount && nightsCount < week ? (
                                <FormattedMessage id="one.week.stay" />
                              ) : null}
                            </div>
                            {!haveExacts && matches && (
                              <div className="condo-result-wrapper__condos-sort">
                                <div className="condo-result-wrapper__sort-select">
                                  <span className="condo-result-wrapper__sort-label">
                                    <FormattedMessage id="sort.by" />:{' '}
                                  </span>
                                  <CustomSelect
                                    disabled={isSessionExpired || (isAnytime && !isSpecificCondo)}
                                    value={sortType}
                                    onChange={this.onSortChange}
                                    options={this.getSortOptions()}
                                  />
                                </div>
                              </div>
                            )}
                          </div>

                          {alternativesMatches.map((condo: ICondo) => {
                            return (
                              <CondoHotelCard
                                onSelect={() => this.selectCondo(condo)}
                                disabled={isSessionExpired}
                                key={condo.condoId}
                                condo={condo}
                                lastRequestType={lastRequestType}
                                onMouseLeave={() => this.onMouseLeaveCondoCard(condo.condoId)}
                                onMouseEnter={() => this.onMouseEnterCondoCard(condo.condoId)}
                                matches={matches}
                              />
                            );
                          })}
                        </>
                      ) : null}
                      {loadingMore ? <CondoCardsSkeleton /> : null}
                    </>
                  ) : null}
                </>
              )}
            </div>
          </div>
          <div
            className={`condo-result-wrapper__map-content ${visibleMap ? 'mobile-show' : ''}`}
            style={{ backgroundImage: `url(${CondoMapPlaceholder})` }}
          >
            {cachedSelectedLocation &&
            condos &&
            !loading &&
            bounds &&
            mapCondos &&
            (this.state.visibleMap || matches) ? (
              <CondoMapWrapper
                selectCondo={this.selectCondo}
                selectedCondo={selectedCondo}
                lastRequestType={lastRequestType}
                disabled={isSessionExpired}
                locations={mapCondos.map(({ condoId, location, pricePerNight, matchType }) => ({
                  id: condoId,
                  location,
                  pricePerNight,
                  isExact: matchType === CondoMatchTypeEnum.Exact && isSpecificCondo,
                  isDisabled:
                    matchType === CondoMatchTypeEnum.Exact && isSpecificCondo && !pricePerNight,
                }))}
                bounds={bounds}
                condos={mapCondos}
                setBounds={this.updateBounds}
                mapOptions={{
                  zoomControl: true,
                  mapTypeControl: false,
                  scaleControl: false,
                  streetViewControl: false,
                  rotateControl: false,
                  fullscreenControl: false,
                  zoomControlOptions: {
                    position: google.maps.ControlPosition.LEFT_TOP,
                  },
                }}
              />
            ) : null}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    condosStore: state.condosStore,
    condosLocationPickerStore: state.condosLocationPickerStore,
    condoFiltersStore: state.condoFiltersStore,
    condoGuestsStore: state.condoGuestsStore,
    menuStore: state.navigationMenuStore,
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<RootState, undefined, Action>,
): IMapDispatchToProps => ({
  setSortType: (sortType: SortTypes) => {
    dispatch(condoFiltersActions.setSortType(sortType));
  },
  setBudgetRanges: (value: string[]) => {
    dispatch(condoFiltersActions.setBudgetRanges(value));
  },
  resetFilters: () => {
    dispatch(condoFiltersActions.resetFilters());
  },
  setPageNumber: (num: number) => {
    dispatch(condosActions.setPageNumber(num));
  },
  getCondos: (searchType: SearchTypeEnum = SearchTypeEnum.Pagination) => {
    dispatch(getCondos(searchType));
  },
  setBounds: (bounds: IBounds) => {
    dispatch(condosActions.setBounds(bounds));
  },
  setSelectedCondo: (condo: ICondo) => {
    dispatch(condosActions.setSelectedCondo(condo));
  },
  addMapCondo: (condo: ICondo) => {
    dispatch(condosActions.addMapCondo(condo));
  },
  setDefaultValues: () => {
    dispatch(condosActions.setDefaultValues());
  },
  resetLocations: () => {
    dispatch(condosLocationPickerActions.resetLocations());
  },
});

export const CondoResultWrapper = connect(
  mapStateToProps,
  mapDispatchToProps,
)(CondoResultWrapperComponent);
